/**
 * VarsityLink Main App Component
 * 
 * This is the entry point of the application that sets up:
 * - Global context providers
 * - Routing configuration
 * - Basic application structure
 * - Company, student, and admin portals
 * - Push notification initialization
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { SuggestionsList } from './features/suggestions';

// Push Notifications Hook
import usePushNotifications from './hooks/usePushNotifications';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';

// Context Providers
import { AuthProvider, useAuth } from './core/contexts/AuthContext';
import { CompanyAuthProvider } from './core/contexts/CompanyAuthContext';
import { AdminProvider, AdminProtectedRoute } from './features/admin';

// Layouts
import MainLayout from './shared/layouts/MainLayout';
import CompanyLayout from './shared/layouts/CompanyLayout';
import AuthLayout from './features/auth/components/AuthLayout';
import CompanyAuthLayout from './features/auth/components/CompanyAuthLayout';
import ProtectedRoute from './shared/components/ProtectedRoute';

// Admin Components
import { AdminLayout, AdminDashboardPage } from './features/admin';
import AdminResourcesPage from './features/admin/pages/AdminResourcesPage';
import AdminCompaniesPage from './features/admin/pages/AdminCompaniesPage';

// Landing Page (Public)
import LandingPage from './features/marketing/components/LandingPage';

// Main Feature Components (Student Portal)
import { HomePage } from './features/home';
import { ProfilePage } from './features/profile';
import { ConnectPage } from './features/connect';

// Social Feature Components
import { SocialPage, PostDetailPage } from './features/social';

// Academic Feature Components
import { 
  AcademicChallenge, 
  LeaderboardPage, 
  AssignmentHub, 
  AssignmentDetail 
} from './features/academic';

// Resources Feature Components
import { ResourcesPage } from './features/resources';

// Messaging Feature Components
import { MessagesPage } from './features/messaging';

// Career Feature Components (Student Portal)
import { 
  CareerOpportunities, 
  AudioScreeningPage, 
  CVManager, 
  JobDetail, 
  MyApplications 
} from './features/career';

// Tutors Feature
import { FindTutors } from './features/tutors';

// Company Feature Components
import { 
  CompanyDashboardPage,
  ManageJobsPage,
  ManageApplicationsPage,
  CompanyProfilePage
} from './features/company';

// Push Notification Settings Component
import PushNotificationSettings from './components/PushNotificationSettings';

// Company Protected Route Component
const CompanyProtectedRoute = ({ children }) => {
  // This would check if user is authenticated as a company
  // For now, we'll create a simple wrapper
  return children;
};

// Push Notification Initializer Component
const PushNotificationInitializer = () => {
  const { currentUser } = useAuth();
  const { permissionStatus, isInitialized } = usePushNotifications();

  useEffect(() => {
    // Register service worker when app starts
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered successfully:', registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  useEffect(() => {
    // Auto-request permission for returning users
    if (currentUser && permissionStatus === 'default') {
      // You can optionally show a prompt here before requesting
      console.log('User logged in, push notifications available');
    }
  }, [currentUser, permissionStatus]);

  return null; // This component doesn't render anything
};

// Placeholder components - Academic features
const StudyPartners = () => (
  <div className="container mx-auto p-4">
    <h1 className="text-2xl font-bold mb-4">Study Partners</h1>
    <p>Find students to study with based on your courses and preferences.</p>
    <div className="mt-4">
      <SuggestionsList 
        limit={10}
        showViewAll={false}
        title="Suggested Study Partners"
      />
    </div>
  </div>
);

const GraduatePrograms = () => (
  <div className="container mx-auto p-4">
    <h1 className="text-2xl font-bold mb-4">Graduate Programs</h1>
    <p>Explore graduate programs and opportunities.</p>
    <p>This feature is coming soon!</p>
  </div>
);

// Career placeholder pages
const CareerCompanies = () => (
  <div className="container mx-auto p-4">
    <h1 className="text-2xl font-bold mb-4">Companies</h1>
    <p>Discover companies and their job opportunities.</p>
    <p>This feature is coming soon!</p>
  </div>
);

// Settings page with push notifications
const SettingsPage = () => (
  <div className="container mx-auto p-4 max-w-4xl">
    <h1 className="text-2xl font-bold mb-6">Settings</h1>
    <div className="space-y-6">
      <PushNotificationSettings />
      {/* Add other settings sections here */}
    </div>
  </div>
);

// Admin placeholder pages (we'll create these next)
const AdminUsersPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">User Management</h1>
    <p>Manage student accounts and profiles.</p>
    <p className="text-gray-600 mt-2">This page is under construction.</p>
  </div>
);

const AdminJobsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Job Postings</h1>
    <p>Monitor and manage job postings.</p>
    <p className="text-gray-600 mt-2">This page is under construction.</p>
  </div>
);

const AdminAnalyticsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Analytics</h1>
    <p>Platform analytics and insights.</p>
    <p className="text-gray-600 mt-2">This page is under construction.</p>
  </div>
);

const AdminModerationPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Content Moderation</h1>
    <p>Review reported content and manage violations.</p>
    <p className="text-gray-600 mt-2">This page is under construction.</p>
  </div>
);

const AdminSystemPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">System Health</h1>
    <p>Monitor system performance and logs.</p>
    <p className="text-gray-600 mt-2">This page is under construction.</p>
  </div>
);

const AdminExportPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Data Export</h1>
    <p>Export system data and generate reports.</p>
    <p className="text-gray-600 mt-2">This page is under construction.</p>
  </div>
);

const AdminSettingsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Admin Settings</h1>
    <p>Configure system settings and preferences.</p>
    <p className="text-gray-600 mt-2">This page is under construction.</p>
  </div>
);

/**
 * Main App Component
 * Sets up the application routing and context providers
 * 
 * @returns {JSX.Element} The App component
 */
const App = () => {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Student Portal Routes */}
        <Route path="/auth" element={
          <AuthProvider>
            <AuthLayout />
          </AuthProvider>
        } />
        
        <Route path="/*" element={
          <AuthProvider>
            <PushNotificationInitializer />
            <NotificationPermissionPrompt />
            <Routes>
              <Route element={<ProtectedRoute />}>
                <Route element={<MainLayout />}>
                  {/* Core Features */}
                  <Route path="home" element={<HomePage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="connect" element={<ConnectPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  
                  {/* Messaging Routes */}
                  <Route path="messages" element={<MessagesPage />}>
                    <Route path=":conversationId" element={null} />
                  </Route>
                  
                  {/* Social Features */}
                  <Route path="social" element={<SocialPage />} />
                  <Route path="social/post/:postId" element={<PostDetailPage />} />
                  
                  {/* Academic Features */}
                  <Route path="academic/challenge" element={<AcademicChallenge />} />
                  <Route path="academic/leaderboard" element={<LeaderboardPage />} />
                  <Route path="assignments" element={<AssignmentHub />} />
                  <Route path="assignments/:assignmentId" element={<AssignmentDetail />} />
                  <Route path="tutors" element={<FindTutors />} />
                  <Route path="study-partners" element={<StudyPartners />} />
                  <Route path="graduate-programs" element={<GraduatePrograms />} />
                  <Route path="resources" element={<ResourcesPage />} />

                  {/* Career Features */}
                  <Route path="career-opportunities" element={<CareerOpportunities />} />
                  <Route path="career-opportunities/:jobId" element={<JobDetail />} />
                  <Route path="career/applications" element={<MyApplications />} />
                  <Route path="career/cv-manager" element={<CVManager />} />
                  <Route path="career/screening" element={<AudioScreeningPage />} />
                  <Route path="career/companies" element={<CareerCompanies />} />
                </Route>
              </Route>
            </Routes>
          </AuthProvider>
        } />
        
        {/* Company Portal Routes */}
        <Route path="/company/auth" element={<CompanyAuthLayout />} />
        <Route path="/company/*" element={
          <CompanyAuthProvider>
            <CompanyProtectedRoute>
              <Routes>
                <Route element={<CompanyLayout />}>
                  <Route path="dashboard" element={<CompanyDashboardPage />} />
                  <Route path="jobs" element={<ManageJobsPage />} />
                  <Route path="applications" element={<ManageApplicationsPage />} />
                  <Route path="profile" element={<CompanyProfilePage />} />
                  <Route path="*" element={<Navigate to="/company/dashboard" replace />} />
                </Route>
              </Routes>
            </CompanyProtectedRoute>
          </CompanyAuthProvider>
        } />

        {/* Admin Portal Routes */}
        <Route path="/admin/*" element={
          <AdminProvider>
            <AdminProtectedRoute>
              <Routes>
                <Route element={<AdminLayout />}>
                  <Route path="dashboard" element={<AdminDashboardPage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="companies" element={<AdminCompaniesPage />} />
                  <Route path="jobs" element={<AdminJobsPage />} />
                  <Route path="resources" element={<AdminResourcesPage />} />
                  <Route path="analytics" element={<AdminAnalyticsPage />} />
                  <Route path="moderation" element={<AdminModerationPage />} />
                  <Route path="system" element={<AdminSystemPage />} />
                  <Route path="export" element={<AdminExportPage />} />
                  <Route path="settings" element={<AdminSettingsPage />} />
                  <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                </Route>
              </Routes>
            </AdminProtectedRoute>
          </AdminProvider>
        } />
      </Routes>
      
      {/* Toast Notifications */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
          success: {
            style: {
              background: 'linear-gradient(to right, #10b981, #14b8a6)',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#10b981',
            },
          },
          error: {
            style: {
              background: 'linear-gradient(to right, #ef4444, #f87171)',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#ef4444',
            },
          },
        }}
      />
    </Router>
  );
};

export default App;